import axios from "axios";
import { getDeviceId, getAdminToken } from "./device";
import { getSavedLang } from "../i18n";
import { getSessionToken } from "./auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  timeout: 600000, // 10 min max for uploads
  withCredentials: true, // send httpOnly session cookie when same-origin
});

// Attach the anonymous device id + optional admin bypass token to every
// request so the server can scope history and enforce (or waive) quotas.
api.interceptors.request.use((config) => {
  const id = getDeviceId();
  if (id) config.headers["X-Device-Id"] = id;
  const admin = getAdminToken();
  if (admin) config.headers["X-Admin-Token"] = admin;
  const lang = getSavedLang();
  if (lang) config.headers["X-Lang"] = lang;
  const tok = getSessionToken();
  if (tok) config.headers["Authorization"] = `Bearer ${tok}`;
  return config;
});

export const getGenres = () => api.get("/genres").then((r) => r.data);

export const getUsage = () => api.get("/usage").then((r) => r.data);

export const detectGenre = (file, onUploadProgress) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post("/detect-genre", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
    })
    .then((r) => r.data);
};

export const startAnalyze = (file, genre, mode, onUploadProgress) => {
  const form = new FormData();
  form.append("file", file);
  form.append("genre", genre);
  form.append("mode", mode);
  return api
    .post("/analyze", form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress,
    })
    .then((r) => r.data);
};

export const getJob = (jobId) => api.get(`/jobs/${jobId}`).then((r) => r.data);

export const getDiskStatus = () => api.get("/disk-status").then((r) => r.data);

export const getAnalysis = (id) => api.get(`/analysis/${id}`).then((r) => r.data);

export const getLufsPresets = () => api.get("/lufs-presets").then((r) => r.data);

export const optimizeTrack = (id, lufsPreset = "genre", lufsTarget = null, useReference = false) =>
  api
    .post(`/optimize/${id}`, { lufs_preset: lufsPreset, lufs_target: lufsTarget, use_reference: useReference })
    .then((r) => r.data);

export const listOptimizations = (id) =>
  // Cache-busting `_t` param so the browser (and any intermediate cache)
  // never returns a stale list right after a new master is committed.
  api.get(`/analysis/${id}/optimizations?_t=${Date.now()}`).then((r) => r.data);

export const canGenerateOptimization = (id, lufsPreset = "genre") =>
  api
    .get(`/analysis/${id}/can-generate`, { params: { lufs_preset: lufsPreset } })
    .then((r) => r.data);

export const optimizePreview = (id, lufsPreset = "genre", lufsTarget = null, useReference = false) =>
  api
    .post(`/optimize/${id}/preview`, { lufs_preset: lufsPreset, lufs_target: lufsTarget, use_reference: useReference })
    .then((r) => r.data);

export const previewUrl = (id, cacheBuster) => {
  const dev = getDeviceId();
  const tok = getSessionToken();
  const admin = getAdminToken();
  const bust = cacheBuster || Date.now();
  const qp = new URLSearchParams({ device_id: dev, t: String(bust) });
  if (tok) qp.set("session_token", tok);
  if (admin) qp.set("admin", admin);
  return `${API}/preview/${id}?${qp.toString()}`;
};

/**
 * Build the URL for a SPECIFIC preview (by preview_id) — used when multiple
 * previews with different LUFS targets exist for the same analysis so the
 * user can A/B compare them in the UI.
 */
export const previewUrlById = (id, previewId, cacheBuster) => {
  const dev = getDeviceId();
  const tok = getSessionToken();
  const admin = getAdminToken();
  const bust = cacheBuster || Date.now();
  const qp = new URLSearchParams({ device_id: dev, t: String(bust) });
  if (tok) qp.set("session_token", tok);
  if (admin) qp.set("admin", admin);
  return `${API}/preview/${id}/${previewId}?${qp.toString()}`;
};

export const audioUrl = (id, which) => {
  const dev = getDeviceId();
  const tok = getSessionToken();
  const admin = getAdminToken();
  const qp = new URLSearchParams({ device_id: dev });
  if (tok) qp.set("session_token", tok);
  if (admin) qp.set("admin", admin);
  return `${API}/audio/${id}/${which}?${qp.toString()}`;
};

export const downloadUrl = (id, optId = null) => {
  const dev = getDeviceId();
  const tok = getSessionToken();
  const admin = getAdminToken();
  const qp = new URLSearchParams({ device_id: dev });
  if (tok) qp.set("session_token", tok);
  if (admin) qp.set("admin", admin);
  const base = optId ? `${API}/download/${id}/${optId}` : `${API}/download/${id}`;
  return `${base}?${qp.toString()}`;
};

export const audioOptimizationUrl = (id, optId) => {
  const dev = getDeviceId();
  const tok = getSessionToken();
  const admin = getAdminToken();
  const qp = new URLSearchParams({ device_id: dev });
  if (tok) qp.set("session_token", tok);
  if (admin) qp.set("admin", admin);
  return `${API}/audio/${id}/optimization/${optId}?${qp.toString()}`;
};

export const pdfUrl = (id, optId = null) => {
  const dev = getDeviceId();
  const lang = getSavedLang();
  const admin = getAdminToken();
  const tok = getSessionToken();
  const qp = new URLSearchParams({ device_id: dev, lang });
  if (admin) qp.set("admin", admin);
  if (tok) qp.set("session_token", tok);
  if (optId) qp.set("opt_id", optId);
  return `${API}/report/${id}.pdf?${qp.toString()}`;
};

export const compareTrack = (id, file) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post(`/compare/${id}`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};

export const uploadReference = (id, file) => {
  const form = new FormData();
  form.append("file", file);
  return api
    .post(`/analysis/${id}/reference`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    })
    .then((r) => r.data);
};

export const deleteReference = (id) =>
  api.delete(`/analysis/${id}/reference`).then((r) => r.data);

// ============ Admin / Whitelist ============
export const adminStats = () => api.get("/admin/stats").then((r) => r.data);
export const adminLlmHealth = () => api.get("/admin/llm-health").then((r) => r.data);
export const adminListWhitelist = () => api.get("/admin/whitelist").then((r) => r.data);
export const adminAddWhitelist = (email, credit_limit, notes = "") =>
  api.post("/admin/whitelist", { email, credit_limit, notes }).then((r) => r.data);
export const adminUpdateWhitelist = (email, patch) =>
  api.patch(`/admin/whitelist/${encodeURIComponent(email)}`, patch).then((r) => r.data);
export const adminDeleteWhitelist = (email) =>
  api.delete(`/admin/whitelist/${encodeURIComponent(email)}`).then((r) => r.data);
export const adminAddCredits = (email, delta, note = "") =>
  api.post(`/admin/whitelist/${encodeURIComponent(email)}/add-credits`, { delta, note }).then((r) => r.data);
export const adminDeleteCreditNote = (email, noteId) =>
  api.delete(`/admin/whitelist/${encodeURIComponent(email)}/credit-notes/${encodeURIComponent(noteId)}`).then((r) => r.data);
export const adminManualRefund = (email, amount, reason) =>
  api.post("/admin/manual-refund", { email, amount, reason }).then((r) => r.data);
export const adminListManualRefunds = (limit = 20) =>
  api.get(`/admin/manual-refunds?limit=${limit}`).then((r) => r.data);
export const adminListRequests = () =>
  api.get("/admin/access-requests").then((r) => r.data);
export const adminApproveRequest = (email, credit_limit, notes = "") =>
  api
    .post(`/admin/access-requests/${encodeURIComponent(email)}/approve`, {
      credit_limit,
      notes,
    })
    .then((r) => r.data);
export const adminRejectRequest = (email) =>
  api.post(`/admin/access-requests/${encodeURIComponent(email)}/reject`).then((r) => r.data);
export const adminAuditLog = (limit = 50) =>
  api.get(`/admin/audit-log?limit=${limit}`).then((r) => r.data);
export const adminDeleteAuditEntry = (logId) =>
  api.delete(`/admin/audit-log/${encodeURIComponent(logId)}`).then((r) => r.data);
export const adminClearAuditLog = () =>
  api.delete("/admin/audit-log").then((r) => r.data);
export const adminDeleteAuditDay = (day) =>
  api.delete(`/admin/audit-log/day/${encodeURIComponent(day)}`).then((r) => r.data);
export const adminDeleteManualRefund = (refundId) =>
  api.delete(`/admin/manual-refunds/${encodeURIComponent(refundId)}`).then((r) => r.data);
export const adminFailuresStats = (hours = 24) =>
  api.get(`/admin/failures/stats?hours=${hours}`).then((r) => r.data);

// Credit-top-up request queue (admin-facing)
export const adminListCreditRequests = () =>
  api.get("/admin/credit-requests").then((r) => r.data);
export const adminApproveCreditRequest = (reqId, granted_amount, admin_note = "") =>
  api
    .post(`/admin/credit-requests/${reqId}/approve`, { granted_amount, admin_note })
    .then((r) => r.data);
export const adminRejectCreditRequest = (reqId, admin_note = "") =>
  api
    .post(`/admin/credit-requests/${reqId}/reject`, { admin_note })
    .then((r) => r.data);

// User-facing credit-request endpoints
export const requestCredits = (requested_amount, message = "") =>
  api.post("/credits/request", { requested_amount, message }).then((r) => r.data);
export const getMyCreditRequest = () =>
  api.get("/credits/my-request").then((r) => r.data);

// ============ Contact form (replaces broken mailto: links) ============
export const contactAdmin = ({ subject, message, category = "general", meta_url = "" }) =>
  api
    .post("/contact", { subject, message, category, meta_url })
    .then((r) => r.data);

// ============ Public blog (external MongoDB, read-only) ============
// We append a `_t` cache buster to every blog request so that CDNs / the
// browser HTTP cache can never serve a stale response — the blog reads live
// from an external DB so deleted articles must disappear immediately.
const _blogBust = () => ({ _t: Date.now() });

export const listBlogArticles = ({ category = null, limit = 50, skip = 0 } = {}) => {
  const params = { limit, skip, ..._blogBust() };
  if (category) params.category = category;
  return api.get("/blog", { params }).then((r) => r.data);
};

export const getBlogCategories = () =>
  api.get("/blog/categories", { params: _blogBust() }).then((r) => r.data);

export const getBlogArticle = (slug) =>
  api
    .get(`/blog/${encodeURIComponent(slug)}`, { params: _blogBust() })
    .then((r) => r.data);

// ============ Pre-launch waitlist ============
export const joinWaitlist = (email, consent, lang) =>
  api
    .post("/waitlist", { email, consent, lang })
    .then((r) => r.data);
export const adminListWaitlist = (limit = 500) =>
  api.get(`/admin/waitlist?limit=${limit}`).then((r) => r.data);
export const adminDeleteWaitlist = (email) =>
  api
    .delete(`/admin/waitlist/${encodeURIComponent(email)}`)
    .then((r) => r.data);
export const adminExportWaitlistCsvUrl = () => `${API}/admin/waitlist.csv`;
